import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import Reveal from "@/components/shared/Reveal";
import Seo from "@/components/shared/Seo";
import Btn from "@/components/shared/Btn";
import SpeakerAvatar from "@/components/shared/SpeakerAvatar";
import { useEditions, registrationCta, editionMonthYear } from "@/lib/useEditions";
import { MapPin, Calendar, ArrowRight } from "lucide-react";

const Edition2026 = () => {
  const params = useParams();
  const slug = params.slug || "2026-paris";
  const { next } = useEditions();
  const { data: ed, isLoading } = useQuery({
    queryKey: ["edition", slug],
    queryFn: async () => (await api.get(`/editions/${slug}`)).data,
  });

  if (isLoading) return <div className="min-h-screen bg-f2b-black" />;
  if (!ed) return <div className="min-h-screen bg-f2b-black flex items-center justify-center text-white pt-32">Édition introuvable.</div>;

  const confs = (ed.conferences || []).filter((c) => c.type === "conference");
  const focus = (ed.conferences || []).filter((c) => c.type === "focus");
  const rc = registrationCta(ed, next);
  const kicker = ed.status === "past" ? "Édition terminée · Rétrospective" : ed.status === "upcoming" ? "Prochaine édition" : "Édition mise en avant";
  const edPremium = (ed.partners || []).filter((p) => p.level === "premium");
  const edOthers = (ed.partners || []).filter((p) => p.level !== "premium");
  const partnersClickable = !!ed.is_active;

  return (
    <>
      <Seo title={`${ed.theme} — FACADES2build ${ed.year}`} description={ed.summary} image={mediaUrl(ed.hero_image)} />

      <section className="relative min-h-[70vh] flex items-end bg-f2b-black" data-testid="edition-hero">
        {mediaUrl(ed.hero_image) && <img src={mediaUrl(ed.hero_image)} alt={ed.theme} className="absolute inset-0 w-full h-full object-cover opacity-45" />}
        <div className="absolute inset-0 bg-gradient-to-t from-f2b-black via-f2b-black/70 to-transparent" />
        <div className="f2b-container relative z-10 pb-20 pt-40">
          <Reveal><p className="f2b-kicker text-f2b-cyan mb-5">{kicker}</p></Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-display font-bold text-white text-4xl md:text-6xl leading-tight max-w-4xl">{ed.theme}</h1>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="flex flex-wrap gap-6 mt-8 text-white/85">
              <span className="flex items-center gap-2"><Calendar size={18} className="text-f2b-cyan" /> {ed.date_label}</span>
              <span className="flex items-center gap-2"><MapPin size={18} className="text-f2b-cyan" /> {ed.venue || ed.city}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* stats */}
      {ed.stats?.length > 0 && (
        <section className="bg-f2b-black border-t border-white/10 py-14">
          <div className="f2b-container grid grid-cols-2 lg:grid-cols-4 gap-8">
            {ed.stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.06} className="border-l-2 border-f2b-cyan pl-5">
                <div className="font-display font-bold text-white text-3xl md:text-4xl">{s.value}</div>
                <div className="text-f2b-gray text-sm mt-1 uppercase tracking-wider">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* summary */}
      <section className="py-24 bg-white">
        <div className="f2b-container max-w-3xl">
          <Reveal>
            <p className="f2b-kicker text-f2b-gray mb-4">Le thème</p>
            <p className="f2b-lead text-f2b-black text-2xl md:text-3xl leading-relaxed">{ed.summary}</p>
          </Reveal>
        </div>
      </section>

      {/* program preview */}
      <section className="py-20 bg-f2b-light">
        <div className="f2b-container">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <Reveal><h2 className="font-display font-bold text-f2b-black text-3xl md:text-4xl">Les conférences</h2></Reveal>
            <Link to={`/programme?edition=${ed.slug}`}><Btn variant="dark" data-testid="edition-programme-complet">Voir le programme complet <ArrowRight size={16} /></Btn></Link>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
            {confs.slice(0, 6).map((c, i) => (
              <Reveal key={c.id} delay={i * 0.05} className="border-t border-f2b-filet pt-5">
                <div className="flex flex-wrap gap-2 mb-2">
                  {c.theme_tags?.map((t) => <span key={t} className="text-xs bg-white border border-f2b-filet px-2 py-1 text-f2b-gray">{t}</span>)}
                </div>
                <h3 className="font-display font-bold text-f2b-black text-xl leading-tight">{c.title}</h3>
                {c.key_takeaway && <p className="f2b-lead text-f2b-gray mt-2 text-sm">« {c.key_takeaway} »</p>}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* focus */}
      {focus.length > 0 && (
        <section className="py-20 bg-white">
          <div className="f2b-container">
            <Reveal><h2 className="font-display font-bold text-f2b-black text-3xl mb-8">Les focus techniques</h2></Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {focus.map((c, i) => (
                <Reveal key={c.id} delay={i * 0.06} className="bg-f2b-black text-white p-7">
                  <span className="f2b-kicker text-f2b-gold">{c.company} · {c.duration}</span>
                  <h3 className="font-display font-bold text-lg mt-3 leading-tight">{c.title}</h3>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* speakers */}
      {ed.speakers?.length > 0 && (
        <section className="py-20 bg-f2b-light">
          <div className="f2b-container">
            <Reveal><h2 className="font-display font-bold text-f2b-black text-3xl mb-8">Intervenants</h2></Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-5">
              {ed.speakers.map((sp, i) => (
                <Reveal key={sp.id} delay={i * 0.04}>
                  <Link to={`/intervenants/${sp.slug}`} className="group block">
                    <div className="aspect-square overflow-hidden mb-2"><SpeakerAvatar speaker={sp} className="group-hover:scale-105 transition-transform duration-500" /></div>
                    <p className="font-display font-bold text-f2b-black text-sm leading-tight">{sp.name}</p>
                    <p className="text-f2b-gray text-xs">{sp.company}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* gallery */}
      {ed.gallery?.length > 0 && (
        <section className="py-20 bg-white">
          <div className="f2b-container">
            <Reveal><h2 className="font-display font-bold text-f2b-black text-3xl mb-8">En images</h2></Reveal>
            <div className="grid md:grid-cols-3 gap-4">
              {ed.gallery.map((g, i) => (
                <Reveal key={i} delay={i * 0.06} className="aspect-[4/3] overflow-hidden">
                  <img src={mediaUrl(g)} alt={`FACADES2build ${ed.year} — ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* partners */}
      {ed.partners?.length > 0 && (
        <section className="py-20 bg-f2b-light">
          <div className="f2b-container">
            <Reveal><h2 className="font-display font-bold text-f2b-black text-3xl mb-8">Partenaires de l'édition</h2></Reveal>
            {edPremium.length > 0 && (
              <div className="mb-10" data-testid="edition-partners-premium">
                <span className="f2b-kicker text-f2b-gold">Partenaire premium</span>
                <div className="mt-4 grid gap-4">
                  {edPremium.map((p) => {
                    const inner = (<>
                      {mediaUrl(p.logo) ? <img src={mediaUrl(p.logo)} alt={p.name} className="max-h-16 max-w-[220px] object-contain" /> : <span className="font-display font-bold text-2xl text-f2b-black">{p.name}</span>}
                      <span className="font-display font-bold text-f2b-black text-lg">{p.name}</span>
                    </>);
                    const cls = "bg-white border border-f2b-gold/40 px-8 py-6 flex items-center gap-6";
                    return partnersClickable
                      ? <Link key={p.id} to="/partenaires" className={cls} data-testid="edition-partner-link">{inner}</Link>
                      : <div key={p.id} className={cls}>{inner}</div>;
                  })}
                </div>
              </div>
            )}
            {edOthers.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {edOthers.map((p) => {
                  const inner = mediaUrl(p.logo) ? <img src={mediaUrl(p.logo)} alt={p.name} className="h-10 object-contain" /> : <span className="font-display font-bold text-f2b-black">{p.name}</span>;
                  const cls = "bg-white border border-f2b-filet px-6 py-4 flex items-center";
                  return partnersClickable
                    ? <Link key={p.id} to="/partenaires" className={cls} data-testid="edition-partner-link">{inner}</Link>
                    : <div key={p.id} className={cls}>{inner}</div>;
                })}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="py-16 bg-f2b-cyan">
        <div className="f2b-container flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-bold text-f2b-black text-2xl md:text-3xl">
              {ed.status === "past" ? "La prochaine édition" : "Participer à cette édition"}
            </h2>
            {ed.status === "past" && next && (
              <p className="text-f2b-black/80 mt-2 max-w-xl">Retrouvez FACADES2build pour l'édition {editionMonthYear(next)}.</p>
            )}
            {ed.status !== "past" && !rc.show && (
              <p className="text-f2b-black/80 mt-2 max-w-xl" data-testid="edition-registration-closed">{ed.registration_closed_message || "Les inscriptions ne sont pas ouvertes actuellement."}</p>
            )}
          </div>
          {rc.show && (rc.external ? (
            <a href={rc.to} target="_blank" rel="noreferrer"><Btn variant="dark" data-testid="edition-cta-inscription">{rc.label} <ArrowRight size={18} /></Btn></a>
          ) : (
            <Link to={rc.to}><Btn variant="dark" data-testid="edition-cta-inscription">{rc.label} <ArrowRight size={18} /></Btn></Link>
          ))}
        </div>
      </section>
    </>
  );
};

export default Edition2026;
